import { usePublicProjectReader, publicQueryKey, projectReaderGet } from '@/lib/public-project-reader';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, apiDelete } from '@/lib/api';
import type { Attachment, CreateAttachmentInput, PresignedUrlInput, PresignedUrlResponse } from '@/lib/schemas';

// Centralized API base URL — same convention as src/lib/api.ts so
// nginx proxies everything through `/api`.
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// ============================================
// QUERY KEYS
// ============================================

export const attachmentKeys = {
    all: ['attachments'] as const,
    lists: () => [...attachmentKeys.all, 'list'] as const,
    list: (taskId: string) => [...attachmentKeys.lists(), taskId] as const,
};

// ============================================
// TYPES
// ============================================

export interface AttachmentsResponse {
    attachments: Attachment[];
    total: number;
}

interface DirectUploadResponse {
    fileKey: string;
    filename: string;
    originalName: string;
    mimeType: string;
    size: number;
}

// ============================================
// HOOKS
// ============================================

export function useAttachments(taskId: string) {
    const reader = usePublicProjectReader();
    return useQuery({
        queryKey: publicQueryKey(reader, attachmentKeys.list(taskId)),
        queryFn: async () => {
            const data = await projectReaderGet<AttachmentsResponse>(reader, `/tasks/${taskId}/attachments`);
            return data;
        },
        refetchInterval: reader ? 10000 : false,
        enabled: !!taskId,
        staleTime: 30 * 1000, // 30 seconds
    });
}

export function usePresignedUrl() {
    return useMutation({
        mutationFn: async (data: PresignedUrlInput) => {
            return apiPost<PresignedUrlResponse>('/uploads/presigned', data);
        },
    });
}

export function useCreateAttachment(taskId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: CreateAttachmentInput) => {
            return apiPost<Attachment>(`/tasks/${taskId}/attachments`, data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: attachmentKeys.list(taskId) });
        },
    });
}

export function useDeleteAttachment(taskId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (attachmentId: string) => {
            return apiDelete<{ message: string }>(`/attachments/${attachmentId}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: attachmentKeys.list(taskId) });
        },
    });
}

export function useDownloadAttachment() {
    return useMutation({
        mutationFn: async (attachmentId: string) => {
            return apiGet<{ downloadUrl: string }>(`/attachments/${attachmentId}/download`);
        },
    });
}

// ============================================
// UPLOAD HELPER - Direct upload through backend
// ============================================

export async function uploadFile(
    file: File,
    onProgress?: (progress: number) => void
): Promise<{ fileKey: string; filename: string; originalName: string; mimeType: string; size: number }> {
    const formData = new FormData();
    formData.append('file', file);

    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable && onProgress) {
                const progress = Math.round((e.loaded / e.total) * 100);
                onProgress(progress);
            }
        });

        xhr.addEventListener('load', () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                try {
                    const response: DirectUploadResponse = JSON.parse(xhr.responseText);
                    resolve({
                        fileKey: response.fileKey,
                        filename: response.filename,
                        originalName: response.originalName,
                        mimeType: response.mimeType,
                        size: response.size,
                    });
                } catch {
                    reject(new Error('Failed to parse upload response'));
                }
            } else {
                reject(new Error(`Upload failed with status ${xhr.status}`));
            }
        });

        xhr.addEventListener('error', () => {
            reject(new Error('Upload failed'));
        });

        xhr.open('POST', `${API_BASE_URL}/uploads/direct`);
        xhr.withCredentials = true; // Include cookies for authentication
        xhr.send(formData);
    });
}
